/**
 * Read-only "Create via API" guide for a capability detail page.
 *
 * Documents how a client platform developer creates a task that runs this
 * capability through the optum-agent API: endpoint, bearer auth, the
 * capability's input fields, and a copyable curl + JSON example. No
 * submission happens here — the Sandbox covers interactive runs.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { getApiClientConfig } from '@optum/api-client/client';
import { getCapabilitySchema } from '@optum/api-client/endpoints/capabilities';
import type { FieldSpec } from '@optum/api-client/types';

import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { JsonViewer } from '../tasks/JsonViewer.js';
import { FRAMEWORK_FIELDS } from './shared/framework-fields.js';

export function CapabilityApiGuide({ capabilityName }: { capabilityName: string }) {
  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityName),
    queryFn: () => getCapabilitySchema(capabilityName),
  });
  const [authMode, setAuthMode] = useState<'oidc' | 'partner'>('oidc');

  if (schema.isPending) return <LoadingBlock />;
  if (schema.isError) {
    return (
      <ErrorBlock
        message={schema.error instanceof Error ? schema.error.message : 'Failed to load schema'}
      />
    );
  }

  const data = schema.data;
  const inputFields = data.input_fields.filter((f) => !FRAMEWORK_FIELDS.has(f.name));
  const baseUrl = getApiClientConfig().baseUrl;

  const bodyExample = {
    agent_id: '<agent-uuid>',
    capability_id: data.id,
    input: exampleInput(inputFields),
  };
  const authHeaders =
    authMode === 'oidc'
      ? ['Authorization: Bearer <access_token>']
      : [
          'Authorization: Bearer fp_<partner_token>',
          'X-Organization-Id: <org-uuid>',
          'X-User-Id: <user-uuid>',
        ];
  const curl = [
    `curl -X POST ${baseUrl}/api/v1/tasks \\`,
    ...[...authHeaders, 'Content-Type: application/json'].map((h) => `  -H "${h}" \\`),
    `  -d '${JSON.stringify(bodyExample)}'`,
  ].join('\n');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        Create a task that runs this capability via the optum-agent API.
      </p>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Typography variant="card">Endpoint</Typography>
        <CodeBlock code={`POST ${baseUrl}/api/v1/tasks`} />
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <Typography variant="card">Authentication</Typography>
        <div
          style={{
            display: 'inline-flex',
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--border-hairline)',
            padding: 5,
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-medium)',
          }}
        >
          {(['oidc', 'partner'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setAuthMode(m)}
              style={{
                borderRadius: 'var(--radius-chip)',
                padding: '5px 11px',
                border: 'none',
                cursor: 'pointer',
                ...(authMode === m
                  ? { background: 'var(--surface-fill)', color: 'var(--text-strong)' }
                  : { background: 'transparent', color: 'var(--text-muted)' }),
              }}
            >
              {m === 'oidc' ? 'User access token' : 'Federated partner'}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          {authMode === 'oidc' ? (
            <>
              Send the user's access token from the identity realm. The caller must be a member of
              the agent&apos;s organization, and{' '}
              <code style={{ fontSize: 'var(--text-meta-lg)' }}>{data.id}</code> must be bound to
              that agent.
            </>
          ) : (
            <>
              Use a federated partner token (
              <code style={{ fontSize: 'var(--text-meta-lg)' }}>fp_…</code>) and identify the acting
              organization and user via headers. The organization must own the agent, and{' '}
              <code style={{ fontSize: 'var(--text-meta-lg)' }}>{data.id}</code> must be bound to
              it.
            </>
          )}
        </p>
        <CodeBlock code={authHeaders.join('\n')} />
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <Typography variant="card">Request body</Typography>
        <ul
          style={{
            marginLeft: 16,
            listStyleType: 'disc',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
            fontSize: 'var(--text-body-md)',
            color: 'var(--text-muted)',
          }}
        >
          <li>
            <code style={{ fontSize: 'var(--text-meta-lg)' }}>agent_id</code> — UUID of the agent
            that runs the task.
          </li>
          <li>
            <code style={{ fontSize: 'var(--text-meta-lg)' }}>capability_id</code> —{' '}
            <code style={{ fontSize: 'var(--text-meta-lg)' }}>{data.id}</code>.
          </li>
          <li>
            <code style={{ fontSize: 'var(--text-meta-lg)' }}>input</code> — object matching the
            fields below.
          </li>
        </ul>
        {inputFields.length === 0 ? (
          <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
            This capability takes no input.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', textAlign: 'left', fontSize: 'var(--text-body-md)' }}>
              <thead style={{ color: 'var(--text-muted)' }}>
                <tr>
                  <th
                    style={{
                      paddingTop: 5,
                      paddingBottom: 5,
                      paddingRight: 11,
                      fontWeight: 'var(--weight-medium)',
                    }}
                  >
                    Field
                  </th>
                  <th
                    style={{
                      paddingTop: 5,
                      paddingBottom: 5,
                      paddingRight: 11,
                      fontWeight: 'var(--weight-medium)',
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      paddingTop: 5,
                      paddingBottom: 5,
                      paddingRight: 11,
                      fontWeight: 'var(--weight-medium)',
                    }}
                  >
                    Required
                  </th>
                  <th
                    style={{ paddingTop: 5, paddingBottom: 5, fontWeight: 'var(--weight-medium)' }}
                  >
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {flattenFields(inputFields).map(({ field: f, depth, path }) => (
                  <tr
                    key={path}
                    style={{
                      borderTop: '1px solid var(--border-hairline)',
                      borderColor: 'var(--border-hairline)',
                      verticalAlign: 'top',
                    }}
                  >
                    <td style={{ paddingTop: 7, paddingBottom: 7, paddingRight: 11 }}>
                      <code
                        style={{
                          fontSize: 'var(--text-meta-lg)',
                          ...{ paddingLeft: `${depth * 1.1}rem` },
                        }}
                      >
                        {depth > 0 && <span style={{ color: 'var(--text-muted)' }}>└ </span>}
                        {f.name}
                      </code>
                    </td>
                    <td
                      style={{
                        paddingTop: 7,
                        paddingBottom: 7,
                        paddingRight: 11,
                        color: 'var(--text-muted)',
                      }}
                    >
                      {fieldType(f)}
                    </td>
                    <td
                      style={{
                        paddingTop: 7,
                        paddingBottom: 7,
                        paddingRight: 11,
                        color: 'var(--text-muted)',
                      }}
                    >
                      {f.required ? 'yes' : 'no'}
                    </td>
                    <td style={{ paddingTop: 7, paddingBottom: 7, color: 'var(--text-muted)' }}>
                      {f.description ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <Typography variant="card">Example</Typography>
        <CodeBlock code={curl} />
        <div
          style={{
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--border-hairline)',
            padding: 11,
          }}
        >
          <div
            style={{ marginBottom: 5, fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
          >
            Request body
          </div>
          <JsonViewer value={bodyExample} defaultExpanded />
        </div>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Typography variant="card">Result</Typography>
        <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          Poll <code style={{ fontSize: 'var(--text-meta-lg)' }}>GET /api/v1/tasks/{'{id}'}</code>{' '}
          for status and{' '}
          <code style={{ fontSize: 'var(--text-meta-lg)' }}>GET /api/v1/tasks/{'{id}'}/output</code>{' '}
          for the result.
        </p>
      </section>
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const toast = useToast();
  return (
    <div style={{ position: 'relative' }}>
      <pre
        style={{
          overflowX: 'auto',
          borderRadius: 'var(--radius-control)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-sunken)',
          padding: 11,
          paddingRight: 64,
          fontSize: 'var(--text-meta-lg)',
          lineHeight: 1.625,
        }}
      >
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(code);
          toast.success('Copied');
        }}
        style={{
          position: 'absolute',
          right: 8,
          top: 8,
          borderRadius: 'var(--radius-chip)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-card)',
          paddingLeft: 7,
          paddingRight: 7,
          paddingTop: 5,
          paddingBottom: 5,
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-muted)',
        }}
      >
        Copy
      </button>
    </div>
  );
}

interface FieldRow {
  field: FieldSpec;
  depth: number;
  path: string;
}

/** Flatten the field tree so nested object/array-item fields show as
 *  indented rows under their parent. */
function flattenFields(fields: FieldSpec[], depth = 0, prefix = ''): FieldRow[] {
  const rows: FieldRow[] = [];
  for (const f of fields) {
    const path = `${prefix}${f.name}`;
    rows.push({ field: f, depth, path });
    const nested =
      f.type === 'object'
        ? f.children
        : f.type === 'array' && f.item?.type === 'object'
          ? f.item.children
          : null;
    if (nested && nested.length > 0) {
      rows.push(...flattenFields(nested, depth + 1, `${path}.`));
    }
  }
  return rows;
}

function fieldType(f: FieldSpec): string {
  if (f.type === 'enum' && f.options && f.options.length > 0) {
    return f.options.map((o) => String(o)).join(' | ');
  }
  if (f.type === 'array' && f.item) {
    return `${f.item.type}[]`;
  }
  return f.type;
}

/** A complete, illustrative `input` skeleton for the docs example. */
function exampleInput(fields: FieldSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    out[f.name] = placeholderFor(f);
  }
  return out;
}

function placeholderFor(f: FieldSpec): unknown {
  if (f.has_default) return f.default;
  switch (f.type) {
    case 'string':
      return 'string';
    case 'integer':
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'datetime':
      return '2026-01-01T00:00:00Z';
    case 'enum':
      return f.options && f.options.length > 0 ? f.options[0] : 'string';
    case 'array':
      return f.item ? [placeholderFor(f.item)] : [];
    case 'object':
      return f.children && f.children.length > 0 ? exampleInput(f.children) : {};
    default:
      return null;
  }
}
